import {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import clsx from 'clsx';

import SortOrderSelect from '../../components/collection/SortOrderSelect.client';
import ProductCard from './Card.client';
import {useScrollFreeze} from '../../hooks/useScrollFreeze';

import {ChevronDownIcon} from '../icons/ChevronDown';
import {useUrl} from '@shopify/hydrogen';

const MobileFilters = ({
  onClick,
  sanityCollection,
  toggleFilter,
  activeFilters,
  isEmpty,
  setActiveFilters,
}: any) => {
  useScrollFreeze();
  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="fixed left-0 top-0 z-[999] h-screen w-full bg-white"
    >
      <div className="h-screen overflow-y-scroll">
        <button
          onClick={onClick}
          className="ml-auto mr-gutter mt-gutter flex h-12 w-12 items-center justify-center rounded-full bg-orange/20"
        >
          <svg
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.5133 1.37924C17.0048 0.870681 16.1804 0.87068 15.6718 1.37924L8.99573 8.05533L2.31964 1.37924C1.81108 0.87068 0.98668 0.870681 0.478121 1.37924C-0.0304373 1.8878 -0.0304365 2.7122 0.478122 3.22075L7.15421 9.89684L0.478121 16.5729C-0.030438 17.0815 -0.030436 17.9059 0.478123 18.4145C0.986681 18.923 1.81108 18.923 2.31964 18.4145L8.99573 11.7384L15.6718 18.4144C16.1804 18.923 17.0048 18.923 17.5133 18.4145C18.0219 17.9059 18.0219 17.0815 17.5133 16.5729L10.8372 9.89685L17.5133 3.22076C18.0219 2.7122 18.0219 1.8878 17.5133 1.37924Z"
              fill="#F27021"
            />
          </svg>
        </button>
        <div className="flex w-full flex-col items-center justify-start gap-y-5 px-gutter py-space-m">
          {sanityCollection?.filters?.map((filter: any, i: number) => (
            <FilterRow
              key={i}
              toggleFilter={toggleFilter}
              index={i}
              activeFilters={activeFilters}
              {...filter}
            />
          ))}
          <button
            onClick={() => setActiveFilters({})}
            className={clsx([
              'transition-standard blockH10 w-full text-left font-bold text-orange underline',
              isEmpty
                ? 'pointer-events-none opacity-0'
                : 'pointer-events-auto opacity-100',
            ])}
          >
            Clear All
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const FilterRow = ({label, tags, toggleFilter, index, activeFilters}: any) => {
  const [activeLabel, setActiveLabel] = useState<string>(
    activeFilters[index] || label,
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    // Check if activeFilters is empty
    if (Object.keys(activeFilters).length === 0) {
      setActiveLabel(label);
      setIsOpen(false);
    }
  }, [activeFilters]);

  return (
    <div className="hover-stroke-white group relative flex w-full flex-col items-center lg:w-auto lg:flex-row">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="blockH10 transition-standard flex w-full items-center justify-between rounded-full bg-light-beige py-3 pl-8 pr-4 font-bold text-orange group-hover:bg-orange group-hover:text-white lg:w-auto lg:min-w-[220px]"
      >
        <span>{activeLabel}</span>
        <ChevronDownIcon
          className={clsx([
            'transition-standard',
            isOpen ? 'rotate-180' : 'rotate-0',
          ])}
        />
      </button>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{height: 0}}
          animate={isOpen ? {height: 'auto'} : {height: 0}}
          exit={{height: 0}}
          className={clsx(
            'left-0 right-auto top-full z-10 mt-3 w-full min-w-[150px] overflow-hidden rounded bg-white shadow lg:absolute',
            'md:left-auto md:right-0',
          )}
        >
          {tags?.map((tag: string, i: number) => (
            <button
              key={i}
              onClick={() => {
                toggleFilter(index, tag);
                setActiveLabel(tag);
                setIsOpen(false);
              }}
              className="flex cursor-pointer flex-row-reverse items-center justify-end gap-x-[10px] whitespace-nowrap p-3 text-left font-bold text-sm"
            >
              <span>{tag}</span>
              <div
                className={clsx([
                  'transition-standard h-5 w-5 rounded-full border',
                  activeFilters[index]?.includes(tag)
                    ? 'border-orange bg-orange'
                    : 'border-dark-grey/20 bg-white',
                ])}
              />
            </button>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default function FiltersAndGrid({
  items,
  sanityCollection,
  hiddenProducts,
  title,
  countryCode,
}: any) {
  const [activeFilters, setActiveFilters] = useState<any>({});

  const toggleFilter = (i: number, value: any) => {
    setActiveFilters({...activeFilters, [i]: value});
  };

  // Check if activeFilters is empty
  const isEmpty = Object.keys(activeFilters).length === 0;

  //Mobile popup state
  const [isOpen, setIsOpen] = useState<boolean>(false);

  //create an array of all store.slug.current values from hiddenProducts
  const hiddenProductsSlugs =
    hiddenProducts?.map((product: any) => product?.store?.slug?.current) || [];

  //create copy of items with items that contain all of the active filters and remove products that are hidden
  const filteredItems = items?.filter((item: any) => {
    //check if item is hidden
    if (hiddenProductsSlugs?.includes(item?.handle)) return false;

    //check if activeFilters is empty
    if (isEmpty) return true;

    //check if item contains all of the active filters
    return Object.keys(activeFilters).every((key: any) => {
      return item?.tags?.includes(activeFilters[key]);
    });
  });

  //GTM
  const {href, pathname} = useUrl();

  useEffect(() => {
    //add page to GTM dataLayer
    window.dataLayer.push({
      event: 'pageview',
      page: {
        title: title,
        path: pathname,
        page: href,
      },
    });
  }, []);

  return (
    <>
      {/* Filters Desktop */}
      <div className="col-start-2 col-end-14 flex h-min w-full items-center justify-center gap-x-5 lg:justify-between">
        <div className="hidden items-center gap-x-5 lg:flex">
          {sanityCollection?.filters?.map((filter: any, i: number) => (
            <FilterRow
              key={i}
              toggleFilter={toggleFilter}
              index={i}
              activeFilters={activeFilters}
              {...filter}
            />
          ))}
          <button
            onClick={() => setActiveFilters({})}
            className={clsx([
              'transition-standard blockH10 font-bold text-orange hover:underline',
              isEmpty
                ? 'pointer-events-none opacity-0'
                : 'pointer-events-auto opacity-100',
            ])}
          >
            Clear All
          </button>
        </div>

        {sanityCollection?.filters && (
          <button
            onClick={() => setIsOpen(true)}
            className={clsx([
              'blockH10 transition-standard  flex  items-center justify-between gap-x-2 rounded-full px-5 py-3 font-bold group-hover:bg-orange group-hover:text-white lg:hidden',
              Object.keys(activeFilters).length !== 0
                ? 'bg-orange text-white'
                : 'bg-light-beige text-orange',
            ])}
          >
            <span>Filters</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.75 4.5L6 8.25L2.25 4.5"
                stroke={
                  Object.keys(activeFilters).length !== 0 ? '#fff' : '#F27021'
                }
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <SortOrderSelect
          key={sanityCollection._id}
          initialSortOrder={sanityCollection.sortOrder}
        />
      </div>

      {/* Products */}
      <div
        className={clsx([
          'col-start-2 col-end-14 grid grid-cols-2 gap-6 pt-space-s lg:grid-cols-5 lg:gap-x-12 lg:gap-y-16',
        ])}
      >
        {filteredItems.length > 0 ? (
          filteredItems?.map((item: any, i: number) => (
            <ProductCard
              key={i}
              storefrontProduct={item}
              countryCode={countryCode}
            />
          ))
        ) : (
          <h2 className="richH2 col-span-full flex items-center justify-center text-center font-bold text-orange">
            No products found
          </h2>
        )}
      </div>

      {/* Filters Mobile */}
      <AnimatePresence mode="wait">
        {isOpen && (
          <MobileFilters
            onClick={() => setIsOpen(false)}
            sanityCollection={sanityCollection}
            toggleFilter={toggleFilter}
            activeFilters={activeFilters}
            isEmpty={isEmpty}
            setActiveFilters={setActiveFilters}
          />
        )}
      </AnimatePresence>
    </>
  );
}
